/* eslint-disable jira/filename-case */
import { stringify as qsStringify } from 'query-string';
import type {
	CmdbObjectId,
	WorkspaceId,
} from '@jira/servicedesk__insight-common-types-shared-types/src/toCmdbObjectTypeId.tsx';
import type {
	AqlSearchRequestQueryParams,
	ObjectByIdActivityRequest,
	ObjectRecentRequest,
	ObjectSchemaQuickSearchObjectRequest,
	ObjectSchemaQuickSearchAttributeRequest,
} from './TypesObjectSchemaQuickSearchAttributeRequest.tsx';
import type { QrCodeObjectRequest } from './TypesQrCodeObjectRequest.tsx';
import { getCmdbRestBaseUrl } from './getCmdbRestBaseUrl.tsx';
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object
 */
const getCmdbObjectUrl = (workspaceId: WorkspaceId) => `${getCmdbRestBaseUrl(workspaceId)}/object`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/${objectId}?includeExtendedInfo=false
 * includeExtendedInfo should be false unless explicitly required
 */
export const getCmdbObjectByIdUrl = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
	includeExtendedInfo = false,
): string => {
	const path = getCmdbObjectByIdUrlWithoutQueryParams(workspaceId, objectId);
	if (!includeExtendedInfo) {
		return `${path}?${qsStringify({ includeExtendedInfo })}`;
	}
	return path;
};

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/${objectId}
 */
export const getCmdbObjectByIdUrlWithoutQueryParams = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
): string => `${getCmdbObjectUrl(workspaceId)}/${objectId}`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/${objectId}/activity
 */
export const getCmdbObjectByIdActivityUrl = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
	queryParams: ObjectByIdActivityRequest,
): string =>
	`${getCmdbObjectByIdUrlWithoutQueryParams(workspaceId, objectId)}/activity?${qsStringify(queryParams)}`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/${objectId}/avatar
 */
export const getCmdbObjectByIdAvatarUrl = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
): string => `${getCmdbObjectByIdUrlWithoutQueryParams(workspaceId, objectId)}/avatar`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/${objectId}/referenceinfo
 */
export const getCmdbObjectByIdReferenceInfoUrl = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
): string => {
	return `${getCmdbObjectByIdUrlWithoutQueryParams(workspaceId, objectId)}/referenceinfo?computeOpenIssues=false`;
};
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/clone/${objectId}
 */
export const getCmdbObjectCloneByIdUrl = (
	workspaceId: WorkspaceId,
	objectId: CmdbObjectId,
): string => `${getCmdbObjectUrl(workspaceId)}/clone/${objectId}`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/create
 */
export const getCmdbObjectCreateUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectUrl(workspaceId)}/create`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/navlist
 */
export const getCmdbObjectNavlistUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectUrl(workspaceId)}/navlist`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/navlist/aql
 */
export const getCmdbObjectNavlistQueryLanguageUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectNavlistUrl(workspaceId)}/aql`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/${objectIdOrKey}/attribute/${objectTypeAttributeId}/referenceValues
 * Returns all reference values for one (object, attribute) pair. Used for on-demand hydration
 * when the navlist response was truncated via limitReferenceValues.
 */
export const getCmdbObjectAttributeReferenceValuesUrl = (
	workspaceId: WorkspaceId,
	objectIdOrKey: string,
	objectTypeAttributeId: string,
): string =>
	`${getCmdbObjectUrl(workspaceId)}/${objectIdOrKey}/attribute/${objectTypeAttributeId}/referenceValues`;
/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/recent
 */
export const getCmdbObjectRecentUrl = (
	workspaceId: WorkspaceId,
	queryParams: ObjectRecentRequest,
): string => `${getCmdbObjectUrl(workspaceId)}/recent?${qsStringify(queryParams)}`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/aql/totalcount
 */
export const getCmdbObjectQueryLanguageTotalCountUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectUrl(workspaceId)}/aql/totalcount`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/search
 */
export const getCmdbObjectSearchUrl = (
	workspaceId: WorkspaceId,
	queryParams: ObjectSchemaQuickSearchObjectRequest,
): string => `${getCmdbObjectUrl(workspaceId)}/search?${qsStringify(queryParams)}`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/searchbyattributes
 */
export const getCmdbAttributeSearchUrl = (
	workspaceId: WorkspaceId,
	queryParams: ObjectSchemaQuickSearchAttributeRequest,
): string => `${getCmdbObjectUrl(workspaceId)}/searchbyattributes?${qsStringify(queryParams)}`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/aql
 */
export const getCmdbObjectQueryLanguageUrl = (
	workspaceId: WorkspaceId,
	queryParams: AqlSearchRequestQueryParams,
): string => `${getCmdbObjectUrl(workspaceId)}/aql?${qsStringify(queryParams)}`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/bulkexport
 */
export const getCmdbAsyncObjectExportUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectUrl(workspaceId)}/bulkexport`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/bulkqrcode
 */
export const getCmdbAsyncBulkQrCodeUrl = (
	workspaceId: WorkspaceId,
	queryParams: QrCodeObjectRequest,
): string => `${getCmdbObjectUrl(workspaceId)}/bulkqrcode?${qsStringify(queryParams)}`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/objecttypeattribute/${objectTypeAttributeId}/generate-ua-duplicates-report
 */
export const getCmdbAsyncGenerateUaDuplicatesReportUrl = (
	workspaceId: WorkspaceId,
	objectTypeAttributeId: string,
): string =>
	`${getCmdbRestBaseUrl(workspaceId)}/objecttypeattribute/${objectTypeAttributeId}/generate-ua-duplicates-report`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/bulkdelete
 */
export const getCmdbAsyncBulkDeleteUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectUrl(workspaceId)}/bulkdelete`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/object/bulkedit
 */
export const getCmdbAsyncBulkEditUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbObjectUrl(workspaceId)}/bulkedit`;

/**
 * /gateway/api/jsm/assets/workspace/${workspaceId}/v1/bundle/instantiate/async
 */
export const getCmdbAsyncCdmBundleInstantiationUrl = (workspaceId: WorkspaceId): string =>
	`${getCmdbRestBaseUrl(workspaceId)}/bundle/instantiate/async`;
